import React, { useEffect, useState } from 'react'
import { Card, Button, Statistic, Table, Typography, Tag, Input, Tooltip, Modal, Space, Empty, App } from 'antd'
import {
  CopyOutlined,
  UserAddOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  QrcodeOutlined,
  TeamOutlined,
  TrophyOutlined,
  FireOutlined,
  WechatOutlined,
  QqOutlined,
  ShareAltOutlined,
  RobotOutlined,
  CloseCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons'
import { motion } from 'framer-motion'
// 假设这是你的API请求服务，请根据实际情况调整路径
import { apiClient } from '@/services/api'
import { COMMON_STYLES } from '@/styles/globalStyles'
import '@/styles/global.css' 

const { Title, Paragraph, Text } = Typography

// --- 类型定义 ---
interface ReferralStats {
  code: string
  shareUrl: string
  totalReferrals: number
  totalRewards: number
  referrals: Array<{
    id: string
    referee: {
      id: string
      username: string
      email?: string
      phone?: string
      createdAt: string
    }
    reward: number
    status: string
    isPaid?: boolean  // 是否付费
    createdAt: string
  }>
}

// 朋友圈推荐文案库
const WECHAT_MOMENTS_CAPTIONS = [
  '🎬 让创意引爆流量，让产品占据焦点！Sora2/Veo3 AI视频创作神器，一键生成影视级产品大片，效果媲美好莱坞！推荐码：{code} 官网：www.duo123.net 🎁 推荐邀请奖5元现金，注册即送50积分，限时福利✨',
  '🚀 让想象成为现实，让创意触手可及！超强AI视频平台，支持Sora2、Veo3等最新模型，秒变专业视频制作大师！推荐码：{code} 官网：www.duo123.net 💰 推荐邀请奖5元，新用户注册送50积分，立即体验～',
  '🔥 让创意引爆流量，让产品占据焦点！Sora2/Veo3一键生成影视产品大片，无需剪辑技能，小白也能做出专业级作品！推荐码：{code} 官网：www.duo123.net 💵 推荐邀请奖5元，注册送50积分，开启创作之旅🎁',
  '✨ 让创意触手可及，让产品占据焦点！影视级AI视频创作平台，Sora2、Veo3轻松制作产品宣传大片，让你的产品瞬间脱颖而出！推荐码：{code} 官网：www.duo123.net 🎉 推荐邀请奖5元，注册送50积分，现在加入还有更多惊喜🚀',
  '💎 让创意引爆流量，让想象成为现实！Sora2/Veo3一键生成影视产品大片，操作超简单，3分钟搞定专业视频！推荐码：{code} 官网：www.duo123.net 💴 推荐邀请奖5元，注册送50积分，错过后悔一整年🎊'
]

const ReferralPage: React.FC = () => {
  const { message: messageApi } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [copied, setCopied] = useState(false)
  const [qrCodeVisible, setQrCodeVisible] = useState(false)
  const [posterImageUrl, setPosterImageUrl] = useState<string | null>(null)
  const [generatingPoster, setGeneratingPoster] = useState(false)
  const [currentCaption, setCurrentCaption] = useState<string>('')

  // --- 加载数据 ---
  const loadStats = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/referral/stats')
      if (response.success && response.data) {
        setStats(response.data)
      }
    } catch (error: any) {
      console.error('加载失败', error)
      // messageApi.error('加载统计失败') 
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStats()
  }, [])

  // --- 工具函数 ---
  const getFullShareUrl = () => {
    if (!stats?.shareUrl) return ''
    return stats.shareUrl.startsWith('http')
      ? stats.shareUrl
      : `${window.location.origin}/${stats.shareUrl.replace(/^\//, '')}`
  }

  const handleCopy = (text: string, type: 'code' | 'link') => {
    if (!text) return
    navigator.clipboard.writeText(text)
    if (type === 'code') {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
    messageApi.success('已复制到剪贴板')
  }

  const handleShareWindow = (type: 'qq' | 'wechat') => {
    const fullUrl = getFullShareUrl()
    if (!fullUrl) return

    if (type === 'qq') {
      const shareText = encodeURIComponent('邀请您加入AI创作平台！注册领积分！')
      const qqUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(fullUrl)}&title=${shareText}`
      window.open(qqUrl, '_blank', 'width=600,height=400')
    } else {
      handleCopy(fullUrl, 'link')
      messageApi.info('链接已复制，请打开微信粘贴分享')
    }
  }

  // 隐藏邮箱中间部分
  const maskEmail = (email?: string) => {
    if (!email) return '未绑定邮箱'
    const [localPart, domain] = email.split('@')
    if (!domain) return email
    if (localPart.length <= 2) {
      return `${localPart}***@${domain}`
    }
    const visibleStart = localPart.substring(0, 2)
    const visibleEnd = localPart.substring(localPart.length - 1)
    return `${visibleStart}***${visibleEnd}@${domain}`
  }

  // 隐藏电话号码中间部分
  const maskPhone = (phone?: string) => {
    if (!phone) return '未绑定手机'
    if (phone.length <= 7) return phone
    const visibleStart = phone.substring(0, 3)
    const visibleEnd = phone.substring(phone.length - 4)
    return `${visibleStart}****${visibleEnd}`
  }

  // 获取随机朋友圈文案
  const getRandomCaption = () => {
    const randomIndex = Math.floor(Math.random() * WECHAT_MOMENTS_CAPTIONS.length)
    const caption = WECHAT_MOMENTS_CAPTIONS[randomIndex]
    // 替换推荐码占位符
    return caption.replace('{code}', stats?.code || '')
  }

  // 复制文案到剪贴板
  const copyCaptionToClipboard = async (caption: string) => {
    try {
      await navigator.clipboard.writeText(caption)
      messageApi.success('朋友圈文案已复制到剪贴板！')
    } catch (error) {
      console.error('复制失败:', error)
      // 降级方案：使用传统方法
      const textarea = document.createElement('textarea')
      textarea.value = caption
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        messageApi.success('朋友圈文案已复制到剪贴板！')
      } catch (err) {
        messageApi.warning('自动复制失败，请手动复制文案')
      }
      document.body.removeChild(textarea)
    }
  }

  // 生成海报图片
  const generatePoster = async () => {
    setGeneratingPoster(true)
    try {
      // 生成随机文案并自动复制
      const caption = getRandomCaption()
      setCurrentCaption(caption)
      
      // 自动复制文案到剪贴板
      await copyCaptionToClipboard(caption)
      const canvas = document.createElement('canvas')
      const dpr = window.devicePixelRatio || 2
      const width = 380 * dpr
      const height = 676 * dpr  // 9:16比例：380 × 16 / 9 = 675.56，取整为676
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        throw new Error('无法创建 Canvas 上下文')
      }

      const scale = dpr
      ctx.scale(scale, scale)

      // 随机背景图片列表（使用本地图片路径）
      const backgroundImages = [
        '/images/poster-bg-1 (1).png  ', // 本地图片1
        '/images/poster-bg-1 (2).png', // 本地图片2
        '/images/poster-bg-1 (3).png', // 本地图片3
        '/images/poster-bg-1 (4).png', // 本地图片4
        '/images/poster-bg-1 (5).png', // 本地图片5
      ]

      // 随机选择背景图片
      const randomBgIndex = Math.floor(Math.random() * backgroundImages.length)
      const selectedBg = backgroundImages[randomBgIndex]

      // 1. 绘制背景图片（保持原图，不添加透明度）
      const bgImage = new Image()
      // 本地图片不需要设置crossOrigin
      
      await new Promise((resolve, reject) => {
        bgImage.onload = () => {
          // 计算图片的原始宽高比，保持比例不变形
          const imageAspectRatio = bgImage.width / bgImage.height
          const targetWidth = 380  // 宽度固定为380px
          const calculatedHeight = targetWidth / imageAspectRatio  // 根据宽高比计算高度
          
          // 使用计算出的高度，保持图片原始宽高比，不添加透明度
          ctx.drawImage(bgImage, 0, 0, targetWidth, calculatedHeight)
          
          resolve(null)
        }
        bgImage.onerror = () => {
          // 如果图片加载失败，使用渐变背景作为后备
          const headerGradient = ctx.createLinearGradient(0, 0, 0, 676)
          headerGradient.addColorStop(0, '#6366f1')
          headerGradient.addColorStop(0.3, '#8b5cf6')
          headerGradient.addColorStop(0.6, '#ec4899')
          headerGradient.addColorStop(1, '#3b82f6')
          ctx.fillStyle = headerGradient
          ctx.fillRect(0, 0, 380, 676)
          resolve(null)
        }
        bgImage.src = selectedBg
      })

      // 2. 绘制二维码（使用推荐链接）
      const sideMargin = 30  // 左右留白 30px
      const qrSize = 65  // 二维码图片大小
      const qrPadding = 4  // padding（优化为4px，适配更小的二维码）
      const qrBoxW = qrSize + qrPadding * 2  // 总宽度：60 + 4*2 = 68px
      const qrBoxH = qrBoxW
      
      // 二维码位置（右下角）
      const qrBoxX = 380 - sideMargin - qrBoxW
      const qrBoxY = 676 - sideMargin - qrBoxH  // 9:16比例，高度676px
      
      // 获取推荐链接
      const shareUrl = getFullShareUrl()
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareUrl)}&margin=0`
      const qrImage = new Image()
      qrImage.crossOrigin = 'anonymous'
      
      await new Promise((resolve, reject) => {
        qrImage.onload = () => {
          // 二维码背景（白色卡片）
          const qrRadius = 8  // 圆角半径（优化为8px，适配60px的二维码）
          ctx.fillStyle = '#ffffff'
          ctx.beginPath()
          ctx.moveTo(qrBoxX + qrRadius, qrBoxY)
          ctx.lineTo(qrBoxX + qrBoxW - qrRadius, qrBoxY)
          ctx.quadraticCurveTo(qrBoxX + qrBoxW, qrBoxY, qrBoxX + qrBoxW, qrBoxY + qrRadius)
          ctx.lineTo(qrBoxX + qrBoxW, qrBoxY + qrBoxH - qrRadius)
          ctx.quadraticCurveTo(qrBoxX + qrBoxW, qrBoxY + qrBoxH, qrBoxX + qrBoxW - qrRadius, qrBoxY + qrBoxH)
          ctx.lineTo(qrBoxX + qrRadius, qrBoxY + qrBoxH)
          ctx.quadraticCurveTo(qrBoxX, qrBoxY + qrBoxH, qrBoxX, qrBoxY + qrBoxH - qrRadius)
          ctx.lineTo(qrBoxX, qrBoxY + qrRadius)
          ctx.quadraticCurveTo(qrBoxX, qrBoxY, qrBoxX + qrRadius, qrBoxY)
          ctx.closePath()
          ctx.fill()
          
          // 边框
          ctx.strokeStyle = '#e5e7eb'
          ctx.lineWidth = 1
          ctx.stroke()

          // 绘制二维码（居中在卡片内）
          const qrImageX = qrBoxX + qrPadding
          const qrImageY = qrBoxY + qrPadding
          ctx.drawImage(qrImage, qrImageX, qrImageY, qrSize, qrSize)
          
          resolve(null)
        }
        qrImage.onerror = reject
        qrImage.src = qrCodeUrl
      })

      const imageUrl = canvas.toDataURL('image/png', 1.0)
      setPosterImageUrl(imageUrl)
      setQrCodeVisible(true)
      // 注意：文案复制已在上面完成，这里不再重复提示
    } catch (error: any) {
      console.error('生成海报失败:', error)
      messageApi.error('生成海报失败，请重试')
    } finally {
      setGeneratingPoster(false)
    }
  }

  // 保存海报图片
  const handleSavePoster = () => {
    if (!posterImageUrl) return
    const link = document.createElement('a')
    link.download = `推荐海报_${stats?.code || 'invite'}.png`
    link.href = posterImageUrl
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    messageApi.success('海报已保存到本地')
  }

  // --- 动画配置 ---
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 50 } }
  }

  // --- 表格列定义 ---
  const columns = [
    {
      title: '被推荐人',
      dataIndex: ['referee', 'username'],
      key: 'username',
      render: (text: string, record: any) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-gray-800">{text}</span>
          <div className="flex flex-col gap-0.5">
            {record.referee.email && (
              <span className="text-xs text-gray-400">{maskEmail(record.referee.email)}</span>
            )}
            {record.referee.phone && (
              <span className="text-xs text-gray-400">{maskPhone(record.referee.phone)}</span>
            )}
            {!record.referee.email && !record.referee.phone && (
              <span className="text-xs text-gray-400">未绑定联系方式</span>
            )}
          </div>
        </div>
      )
    },
    {
      title: '注册时间',
      dataIndex: ['referee', 'createdAt'],
      key: 'createdAt',
      render: (date: string) => <span className="text-gray-500">{new Date(date).toLocaleString('zh-CN', { hour12: false })}</span>
    },
    {
      title: '充值奖',
      dataIndex: 'isPaid',
      key: 'isPaid',
      render: (isPaid: boolean) =>
        isPaid ? (
          <Tag color="success" bordered={false} icon={<GiftOutlined />} className="bg-red-50 text-red-600 border-red-200">
            现金奖￥5
          </Tag>
        ) : (
          <Tag color="default" bordered={false}>未充值+0元</Tag>
        )
    },
    {
      title: '积分奖',
      key: 'rewardStatus',
      render: (record: any) => {
        const { reward, status } = record
        if (status === 'COMPLETED') {
          return (
            <Tag 
              color="success" 
              bordered={false}
              className="bg-emerald-50 text-emerald-600 border-emerald-100"
            >
              +{reward}积分
            </Tag>
          )
        } else {
          return (
            <Tag color="warning" bordered={false}>
              <span className="flex items-center gap-1.5">
                <GiftOutlined />
                待完成{reward}积分
              </span>
            </Tag>
          )
        }
      }
    }
  ]

  return (
    <div className="min-h-screen w-full relative">
      {/* 内容区域 - 应用统一样式 */}
      <div className="unified-container relative z-10" style={{
        paddingBottom: 'var(--spacing-xxl)',
        paddingTop: 'var(--spacing-sm)'
      }}>
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col"
        >
        {/* 1. 头部标题区 */}
        <div className="w-full page-header-section">
          <motion.div
            {...COMMON_STYLES.pageEnter}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between"
            style={{
              marginBottom: 'var(--spacing-lg)',
              gap: 'var(--spacing-md)'
            }}
          >
            {/* 左侧：标题和副标题 */}
            <div className="flex-1">
              <div className="flex items-center" style={{
                gap: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                <span style={{ fontSize: 'var(--font-h1-mobile)' }}>🎁</span>
                <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  推荐奖励
                </h1>
              </div>
              <Paragraph className="unified-text-secondary" style={{
                fontSize: 'var(--font-body)',
                marginLeft: 0,
                marginTop: 'var(--spacing-xs)',
                marginBottom: 0
              }}>
                每邀请一位好友注册，奖励你<span className="text-emerald-500 font-bold">50积分</span>，好友获得 <span className="text-blue-500 font-bold">30积分</span>，充值成功奖你<span className="text-red-500 font-bold">5元</span>
              </Paragraph>
            </div>
            
            {/* 右侧：特色标签 */}
            <div className="flex flex-wrap md:justify-end" style={{ gap: 'var(--spacing-sm)' }}>
              <Tag icon={<FireOutlined />} className="unified-tag bg-gradient-to-r from-red-50 to-red-100 text-red-700">
                永久有效
              </Tag>
              <Tag icon={<TrophyOutlined />} className="unified-tag bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700">
                实时到账
              </Tag>
            </div>
          </motion.div>
        </div>

        {/* 2. 核心数据区 (Grid布局) */}
        <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: 'var(--spacing-xl)' }}>
          {/* 左侧：推荐码 Hero Card */}
          <motion.div variants={itemVariants} className="lg:col-span-2 h-full">
            <div 
              className="relative overflow-hidden bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-lg text-white h-full flex flex-col" 
              style={{ 
                padding: 'var(--spacing-xl)'
              }}
            >
              {/* 背景装饰图案 */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
              
              <div className="flex justify-between items-start relative z-10" style={{ marginBottom: 'var(--spacing-xl)' }}>
                <div>
                  <h3 className="text-lg font-medium text-white/90 flex items-center gap-2">
                    <ShareAltOutlined /> 推广收益
                  </h3>
                  <p className="text-sm text-white/70 mt-1">好友注册时，自带推荐码</p>
                </div>
                <Button 
                  type="default" 
                  shape="circle" 
                  ghost 
                  icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />} 
                  onClick={() => handleCopy(stats?.code || '', 'code')}
                  className="!text-white !border-white/40 hover:!bg-white/20 hover:!border-white"
                />
              </div>

              <div className="flex-1 flex flex-col justify-center items-center relative z-10" style={{ paddingTop: 'var(--spacing-xl)', paddingBottom: 'var(--spacing-xl)' }}>
                {stats?.code ? (
                  <div 
                    className="text-5xl md:text-6xl font-black tracking-widest cursor-pointer select-all transition-transform hover:scale-105 active:scale-95"
                    onClick={() => handleCopy(stats?.code || '', 'code')}
                  >
                    {stats.code}
                  </div>
                ) : (
                  <div className="animate-pulse bg-white/20 h-16 w-48 rounded"></div>
                )}
                <div className="mt-2 text-white/60 text-sm">点击复制推荐码</div>
              </div>

              {/* 底部快速操作 */}
              <div className="mt-auto border-t border-white/20 relative z-10 flex flex-col md:flex-row items-center justify-between" style={{ 
                paddingTop: 'var(--spacing-xl)',
                gap: 'var(--spacing-lg)'
              }}>
                <div className="text-sm text-white/80">
                  <span className="mr-2">💡 海报分享:</span>
                  朋友圈和短视频或直播间，成功率提升 50%
                </div>
                <Button 
                  onClick={generatePoster}
                  loading={generatingPoster}
                  className="bg-white/20 border-none text-white hover:!bg-white hover:!text-purple-600 backdrop-blur-sm shadow-sm"
                  icon={<QrcodeOutlined />}
                >
                  {generatingPoster ? '生成中...' : '获取专属推广海报'}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* 右侧：统计卡片组 */}
          <div className="flex flex-col lg:col-span-1" style={{ gap: 'var(--spacing-xl)' }}>
            {/* 推荐人数 */}
            <motion.div variants={itemVariants} className="flex-1">
              <Card bordered={false} className="h-full shadow-sm hover:shadow-md transition-shadow rounded-2xl">
                <Statistic
                  title={<span className="text-gray-500 font-medium">累计推荐人数</span>}
                  value={stats?.totalReferrals || 0}
                  prefix={
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3 text-blue-500">
                      <TeamOutlined className="text-xl" />
                    </div>
                  }
                  valueStyle={{ fontWeight: 'bold', fontSize: '2rem' }}
                />
              </Card>
            </motion.div>

            {/* 获得奖励 */}
            <motion.div variants={itemVariants} className="flex-1">
              <Card bordered={false} className="h-full shadow-sm hover:shadow-md transition-shadow rounded-2xl">
                <Statistic
                  title={<span className="text-gray-500 font-medium">累计获得奖励</span>}
                  value={stats?.totalRewards || 0}
                  precision={0}
                  prefix={
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mr-3 text-emerald-500">
                      <GiftOutlined className="text-xl" />
                    </div>
                  }
                  suffix={<span className="text-sm text-gray-400 font-normal ml-1">积分</span>}
                  valueStyle={{ fontWeight: 'bold', fontSize: '2rem', color: '#10b981' }}
                />
              </Card>
            </motion.div>
          </div>
        </div>

        {/* 3. 分享链接区 */}
        <motion.div variants={itemVariants} style={{ marginTop: 'var(--spacing-xl)' }}>
          <Card bordered={false} className="rounded-2xl shadow-sm">
            <div className="flex flex-col md:flex-row items-center" style={{ gap: 'var(--spacing-xl)' }}>
               <div className="flex-1 w-full">
                  <div className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                    分享推广链接
                  </div>
                  <div className="flex" style={{ gap: 'var(--spacing-sm)' }}>
                    <Input 
                      value={getFullShareUrl()} 
                      readOnly 
                      className="bg-gray-50 border-gray-200 rounded-lg text-gray-500"
                    />
                    <Button type="primary" icon={<CopyOutlined />} onClick={() => handleCopy(getFullShareUrl(), 'link')} className="rounded-lg bg-blue-600">
                      复制
                    </Button>
                  </div>
               </div>
               
               <div className="w-full md:w-auto flex flex-col items-start md:items-end">
                  <div className="text-sm font-bold text-gray-700 mb-2">快速分享至</div>
                  <Space>
                    <Tooltip title="分享到微信">
                      <Button 
                        shape="circle" 
                        icon={<WechatOutlined />} 
                        size="large"
                        className="text-green-600 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300"
                        onClick={() => handleShareWindow('wechat')}
                      />
                    </Tooltip>
                    <Tooltip title="分享到QQ">
                      <Button 
                        shape="circle" 
                        icon={<QqOutlined />} 
                        size="large"
                        className="text-blue-500 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300"
                        onClick={() => handleShareWindow('qq')}
                      />
                    </Tooltip>
                  </Space>
               </div>
            </div>
          </Card>
        </motion.div>

        {/* 4. 记录列表 */}
        <motion.div variants={itemVariants} style={{ marginTop: 'var(--spacing-xl)', marginBottom: 'var(--spacing-xl)' }}>
          <Card 
            bordered={false} 
            className="rounded-2xl shadow-sm overflow-hidden"
            title={
              <div className="flex items-center gap-2">
                <span className="w-1 h-4 bg-purple-500 rounded-full"></span>
                <span>最近邀请记录</span>
                <span className="text-xs text-gray-400 font-normal">- 状态实时更新 充值奖励5元/人</span>
              </div>
            }
          >
            <Table
              columns={columns}
              dataSource={stats?.referrals || []}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 5, hideOnSinglePage: true }}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无邀请记录，快去分享吧" />
              }}
              rowClassName="hover:bg-gray-50 transition-colors"
            />
          </Card>
        </motion.div>

        {/* 5. 海报风格二维码弹窗 - 显示合成后的海报图片 */}
        <Modal
          open={qrCodeVisible}
          onCancel={() => {
            setQrCodeVisible(false)
            setPosterImageUrl(null)
            setCurrentCaption('')
          }}
          footer={null}
          centered
          width={400}
          styles={{ 
            body: { 
              padding: posterImageUrl ? '20px' : '40px', 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            } 
          }}
          closeIcon={posterImageUrl ? <CloseCircleOutlined style={{ fontSize: '20px', color: '#666' }} /> : null}
          maskStyle={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          {posterImageUrl ? (
            <div className="flex flex-col items-center w-full" style={{ gap: 'var(--spacing-lg)' }}>
              <img 
                src={posterImageUrl} 
                alt="推荐海报" 
                className="w-full max-w-[360px] rounded-xl shadow-lg"
                style={{ maxHeight: '70vh', objectFit: 'contain' }}
              />
              
              {/* 朋友圈文案显示区域 */}
              {currentCaption && (
                <div className="w-full bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <Text className="text-sm font-semibold text-gray-700">📝 朋友圈文案（已自动复制）</Text>
                    <Button 
                      type="link" 
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyCaptionToClipboard(currentCaption)}
                      className="p-0 h-auto"
                    >
                      再次复制
                    </Button>
                  </div>
                  <div 
                    className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-white rounded p-3 border border-gray-100"
                    style={{ 
                      maxHeight: '120px',
                      overflowY: 'auto',
                      lineHeight: '1.6'
                    }}
                  >
                    {currentCaption}
                  </div>
                </div>
              )}
              
              <div className="flex w-full" style={{ gap: 'var(--spacing-md)' }}>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleSavePoster}
                  block
                  className="bg-blue-600"
                >
                  保存海报
                </Button>
                <Button 
                  onClick={() => {
                    setQrCodeVisible(false)
                    setPosterImageUrl(null)
                    setCurrentCaption('')
                  }}
                  block
                >
                  关闭
                </Button>
              </div>
              <Text type="secondary" className="text-xs">
                长按图片可保存，复制文案后可直接分享到朋友圈
              </Text>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin text-4xl mb-4">⏳</div>
              <Text>正在生成海报...</Text>
            </div>
          )}
        </Modal>
        </motion.div>
      </div>
    </div>
  )
}

export default ReferralPage